import { HttpClient } from '@angular/common/http';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  linkedSignal,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { PageEvent } from '@angular/material/paginator';
import { translate } from '@jsverse/transloco';

import { toCanvas } from 'html-to-image';
import { Moment } from 'moment';
import { firstValueFrom } from 'rxjs';
import { CalendarData, Calendar_onValueChangeEvent, DropdownObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { ChartDisplayOptions } from 'app/models';
import { WidgetDataSource } from 'app/models/widget-data-source.model';
import { DownloadAsDialogResult } from 'app/modules/collection/components/download-as-dialog/download-as-dialog.component';
import {
  DATE_WITHOUT_TIMEZONE_FORMAT,
  FilterService,
} from 'app/modules/collection/services/filter.service';
import { DashboardViewComponent } from 'app/modules/dashboard/pages/dashboard-view/dashboard-view.component';
import { ChartCustomColorService } from 'app/modules/dashboard/services/chart-custom-color.service';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { Comment, CommentPoint, Widget } from 'app/providers/bridge-api/dashboard.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import {
  ContentChannel,
  ContentChannelService,
} from 'app/providers/storage-api/content-channel.service';
import { DocumentService } from 'app/providers/storage-api/document.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { InteractiveSmartSummaryService } from 'app/providers/storage-api/interactive-smart-summary.service';
import {
  DEFAULT_PROJECT_COLOR_PALETTE,
  ProjectService,
} from 'app/providers/storage-api/project.service';
import { Report, ReportService } from 'app/providers/storage-api/report.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { TimezoneService } from 'app/services/timezone.service';
import { WidgetLimiterService } from 'app/services/widget-limiter.service';
import { FilterUtils } from 'app/utils/filter.utils';
import { Utils } from 'app/utils/utils';

import { NumericMetadataField } from '../numeric-metadata-field-selector/numeric-metadata-field-selector.models';
import { AbstractTableWidgetComponent } from '../widget/abstract-table-widget.component';
import { WidgetComponent } from '../widget/widget.component';
import { IsValidNumberPipe } from '../widget-big-number/is-valid-number.pipe';
import { WidgetChartComponent, WidgetChartOptions } from '../widget-chart/widget-chart.component';
import { ChartCommentsService } from '../../services/chart-comments.service';
import { PptxReport } from '../../services/pptx.service';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';
import { WidgetChartModule } from '../../widget-chart.module';
import {
  JourneyDetailsDialogComponent,
  JourneyDetailsDialogOptions,
} from './journey-details-dialog/journey-details-dialog.component';

export interface WidgetJourneyOptions extends WidgetChartOptions {
  numericMetadataFields: NumericMetadataField[];
  deselectedChannelFilter?: string[];
  userQuery?: string;
  selectedRelativeRange?: string;
  customDateRange?: { startDate: Date; endDate: Date };
}

export interface JourneyRow {
  subjectId: string;
  totalTouchpoints: number;
  [key: string]: string | number;
}

interface JourneyTimelineLegendItem {
  color: string;
  label: string;
}

type JourneyReport = Report<JourneyReportItems> & {
  pagination: { afterKey: string; pageSize: number; totalCount: number };
};

interface JourneyReportItems {
  [key: string]: JourneyReportItem;
}

interface JourneyReportItem {
  subject: string;
  subjectValue: string;
  totalDoc: number;
  contentChannels: ContentChannelItem[];
  metadata: JourneyReportMetadata[];
}

interface JourneyReportMetadata {
  docs: number;
  field: string;
  score: number;
  scoreType: JourneyScoreType;
  firstValue?: number;
  lastValue?: number;
  population?: number;
  total?: number;
}

export type JourneyScoreType = 'AVERAGE' | 'LAST' | 'DELTA';

interface ContentChannelItem {
  id: string;
  label: string;
  docs: number;
}

export interface JourneyTableColumns {
  [def: string]: JourneyTableColumn;
}

interface JourneyTableColumn {
  label: string;
}
export interface JourneyDrillLevel {
  dateInterval: string;
  dateRange: { fromDate: Moment; toDate: Moment } | null;
  previousDateInterval: string | null;
}

export interface JourneyDrillRange {
  min: number;
  max: number;
  dataMin: number;
  dataMax: number;
  dateInterval: string;
  previousDateInterval: string | null;
  dateFilter: string;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-journey',
  templateUrl: './widget-journey.component.html',
  styleUrls: ['./widget-journey.component.scss'],
  imports: [AppSharedModule, WidgetChartModule, IsValidNumberPipe, JourneyDetailsDialogComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: {
    '(document:click)': 'closeSettingsPopup()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetJourneyComponent
  extends AbstractTableWidgetComponent<WidgetJourneyOptions>
  implements OnInit
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly httpClient = inject(HttpClient);
  private readonly chartCommentsService = inject(ChartCommentsService);

  nmotChart = viewChild<WidgetChartComponent>('nmotChartRef');
  timelineChart = viewChild<WidgetChartComponent>('timelineChartRef');
  @ViewChild('table', { read: ElementRef }) table: ElementRef<HTMLTableElement>;
  @ViewChild('chartContainer') chartContainer: ElementRef<HTMLDivElement>;

  columns = signal<JourneyTableColumns>({});
  displayedColumns = signal<string[]>(['subjectId', 'totalTouchpoints']);
  dataSource = signal<JourneyRow[]>([]);
  subjectColumnHeader = signal<string>('');
  private analyticItemColumns = new Set<string>();
  private defaultField = '';

  pageIndex = signal(0);
  pageCount = signal(0);
  pageSize = signal(20);
  subjectAfterKey = signal('');
  private pageAfterKeys: string[] = [''];

  isLoading = false;
  calendarMode = signal<string>('relative');
  calendarCheckedId = signal<string | null>('all');
  calendarPlaceholder = signal<string>(translate('widgets.journey.all'));
  calendarData = signal<CalendarData>({
    initialDateRange: { first: undefined, last: undefined },
    preselectedCalendarListOptions: [],
  });
  searchText = signal('');
  showJourneyDetailsDialog = signal(false);
  journeyDetailsDialogOptions = signal<JourneyDetailsDialogOptions | null>(null);

  nmotWidget = signal<Widget<WidgetChartOptions> | null>(null);
  timelineSubject = signal<string | null>(null);
  timelineWidget = signal<Widget<WidgetChartOptions> | null>(null);
  sharedReportResponse = signal<any>(null);
  nmotPreloadedResponse = signal<any>(null);
  private latestTimelineResponse: any = null;
  private pendingTimelineWidget: Widget<WidgetChartOptions> | null = null;
  private currentRow: JourneyRow | null = null;
  private contentChannelParentMap: Record<string, string> = {};
  private contentChannelLabelMap: Record<string, string> = {};
  private contentChannelsBySubject: Record<string, ContentChannelItem[]> = {};
  private nmotDrillDateFilterValue: string | null = null;
  /** Coalesces the plot area syncs requested by a burst of NMOT renders into a single one. */
  private plotAreaSyncTimer: ReturnType<typeof setTimeout> | null = null;
  private commentsSeeded = linkedSignal<Widget<WidgetChartOptions> | null, boolean>({
    source: this.nmotWidget,
    computation: () => false,
  });

  availableSubjects = signal<DropdownObject[]>([]);
  selectedContentChannels = signal<string[]>([]);
  availableContentChannels = signal<DropdownObject[]>([]);
  timelineLegendItems = signal<JourneyTimelineLegendItem[]>([]);
  timelinePreviousDateInterval = signal<string | null>(null);
  isCompact = signal(false);
  showSettingsPopup = signal(false);
  private suppressChannelFilterChange = false;
  timelineBackLabel = computed(() => {
    const previousDateInterval = this.timelinePreviousDateInterval();
    if (!previousDateInterval) {
      return translate('widgets.journey.full-timeline');
    }
    return translate('widgets.journey.back-to-drill', {
      level: translate(
        `widgets.journey.drill-intervals.${this.getJourneyDrillIntervalLabelKey(previousDateInterval)}`
      ),
    });
  });

  constructor(
    private reportService: ReportService,
    protected filterService: FilterService,
    protected dialogService: DialogService,
    protected documentService: DocumentService,
    protected projectService: ProjectService,
    protected alertService: AlertService,
    protected widgetLimiterService: WidgetLimiterService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected dashboardViewComponent: DashboardViewComponent,
    protected widgetComponent: WidgetComponent,
    protected systemService: SystemService,
    protected accountService: StorageAccountService,
    protected authService: AuthService,
    protected timezoneService: TimezoneService,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService,
    protected contentChannelService: ContentChannelService
  ) {
    super(
      filterService,
      dialogService,
      documentService,
      projectService,
      alertService,
      widgetLimiterService,
      interactiveSmartSummaryService,
      dashboardViewComponent,
      widgetComponent,
      systemService,
      accountService,
      authService,
      timezoneService,
      customFieldService,
      commentsHandlerService
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.contentChannelService.getContentChannels().then((tree) => {
      this.contentChannelParentMap = this.buildChildToParentMap(tree);
      this.contentChannelLabelMap = this.buildIdToLabelMap(tree);
    });

    this.refresh(false, true);
    this.setupResizeObserver();
  }

  private initializeCalendar() {
    const allOption = {
      id: 'all',
      title: translate('widgets.journey.all'),
      first: new Date(this.widgetDataSource.minDocAt),
      last: new Date(this.widgetDataSource.maxDocAt),
    };

    const options = [allOption, ...this.filterService.generateRelativeRangeOptions()];

    const savedRelative = this.widget.payload.selectedRelativeRange ?? 'all';
    const customRange = this.widget.payload.customDateRange;
    const setCalendarData = (mode: 'fixed' | 'relative', calendarData, selectedId = null) => {
      this.calendarMode.set(mode);
      this.calendarCheckedId.set(selectedId);
      this.calendarData.set(calendarData);
    };

    if (savedRelative === 'custom' && customRange) {
      setCalendarData('fixed', {
        initialDateRange: {
          first: new Date(customRange.startDate),
          last: new Date(customRange.endDate),
        },
        preselectedCalendarListOptions: options,
      });
      return;
    }

    this.widget.payload.selectedRelativeRange = savedRelative;
    delete this.widget.payload.customDateRange;

    const selectedOption = options.find((opt) => opt.id === savedRelative);
    setCalendarData(
      'relative',
      {
        initialDateRange: {
          first: selectedOption?.first,
          last: selectedOption?.last,
        },
        preselectedCalendarListOptions: options,
      },
      savedRelative
    );
  }

  get defaultTitle(): string {
    return translate('widgets.title.journey');
  }

  public async refresh(
    reset?: boolean,
    initialLoading?: boolean,
    refreshedDatasource?: WidgetDataSource
  ) {
    super.refresh(reset, initialLoading, refreshedDatasource);
    if (this.dataSourceNotFound) {
      this.loadingStarted.emit(initialLoading);
      this.loadingFinished.emit(initialLoading);
      this.isLoading = false;
      return;
    }
    this.initializeCalendar();
    if (reset) {
      this.resetPagination();
    }

    if (!this.widgetDataSource.isJourneyGroup) {
      this.clearJourneyData();
      this.loadingStarted.emit(initialLoading);
      this.loadingFinished.emit(initialLoading);
      this.isLoading = false;
      return;
    }
    this.noData = false;

    this.loadingStarted.emit(initialLoading);
    this.isLoading = true;
    this.cdr.markForCheck();
    try {
      this.defaultField = this.widgetDataSource.journeyGroupRule.defaultField;
      this.subjectColumnHeader.set(
        Utils.capitalize(FieldService.getFieldLongName(this.defaultField))
      );

      const report = await this.getReport();

      await this.processReport(report);
    } catch (e) {
      this.alertService.showErrorToast(
        translate('widgets.journey.error-loading-journey-report'),
        e
      );
      throw e;
    } finally {
      this.loadingFinished.emit(initialLoading);
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private async getReport() {
    const params = {
      subject: this.defaultField,
      subjectSize: this.pageSize(),
      subjectAfterKey: this.pageIndex() === 0 ? '' : this.subjectAfterKey(),
      subjectValue: this.searchText() ? `${this.searchText()}*` : undefined,
      timezone: this.timezoneService.timezone,
      weightField: this.payload.weightField,
    };
    const body: { customField: string; scoringStrategy: string }[] =
      this.payload.numericMetadataFields.map((f) => ({
        customField: f.fieldId,
        scoringStrategy: f.scoring,
      }));

    // Use calendar selection for both table and timeline views
    const dateSelection = this.widget.payload.selectedRelativeRange;
    const customRange = this.widget.payload.customDateRange;
    let dateFilter = dateSelection;

    this.widget.payload.userQuery = undefined;
    if (dateSelection === 'custom') {
      const start = this.timezoneService
        .padToStart(customRange.startDate)
        .format(DATE_WITHOUT_TIMEZONE_FORMAT);
      const end = this.timezoneService
        .padToEnd(customRange.endDate)
        .format(DATE_WITHOUT_TIMEZONE_FORMAT);
      dateFilter = `daterange/${start}/${end}`;
    }

    if (dateSelection !== 'all') {
      this.widget.payload.userQuery = `createdAt:${FilterUtils.computeValueForDateFilter(dateFilter)}`;
    }

    return this.reportService.postReport(
      this.widgetDataSource.collectionName,
      this.widgetDataSource.groupId,
      'journeyGroup',
      params,
      body,
      'json',
      this.filterService.getQueryForDocuments(this.widgetQueryOptions, false, true),
      undefined,
      undefined,
      this.payload.projectId
    ) as Promise<JourneyReport>;
  }

  private async processReport(report: JourneyReport): Promise<void> {
    const afterKey = report.pagination?.afterKey ?? null;
    if (afterKey !== null) {
      this.subjectAfterKey.set(afterKey);
      this.pageAfterKeys[this.pageIndex() + 1] = afterKey;
    }
    this.pageCount.set(report.pagination.totalCount);

    const sortedChannels = [
      ...new Set(
        Object.values(report.items).flatMap((item) => item.contentChannels.map((cc) => cc.id))
      ),
    ].sort();

    const cols: JourneyTableColumns = {
      subjectId: { label: this.subjectColumnHeader() },
    };

    this.analyticItemColumns.clear();
    this.payload.numericMetadataFields.forEach((f, i) => {
      const key = `${f.fieldId}_${i}`;
      this.analyticItemColumns.add(key);
      cols[key] = {
        label: `${f.label} (${this.formatScoringLabel(f.scoring)})`,
      };
    });

    cols['totalTouchpoints'] = { label: translate('widgets.journey.total-touchpoints') };

    const channelLabelMap = new Map(
      Object.values(report.items).flatMap((item) =>
        item.contentChannels.map((cc) => [cc.id, cc.label] as const)
      )
    );

    for (const channel of sortedChannels) {
      cols[channel] = { label: channelLabelMap.get(channel) ?? channel };
    }

    this.columns.set(cols);
    this.displayedColumns.set(Object.keys(cols));
    const dataItems: JourneyRow[] = Object.values(report.items).map((value) => {
      const row: JourneyRow = {
        subjectId: value.subjectValue,
        totalTouchpoints: value.totalDoc,
      };
      this.payload.numericMetadataFields.forEach((f, i) => {
        const meta = value.metadata[i];
        row[`${f.fieldId}_${i}`] = meta.score;
      });
      value.contentChannels.forEach((cc) => {
        row[cc.id] = cc.docs;
      });
      return row;
    });
    if (dataItems.length === 0) {
      this.clearJourneyData(false);
      return;
    }

    this.dataSource.set(dataItems);

    const newAvailableSubjects = dataItems.map((item) => ({
      id: String(item.subjectId),
      title: String(item.subjectId),
    }));

    // Reset currentRow to the first available subject if the previous one is gone
    if (this.currentRow) {
      const currentId = String(this.currentRow.subjectId);
      const stillExists = dataItems.find((r) => String(r.subjectId) === currentId);
      if (!stillExists && dataItems.length > 0) {
        this.currentRow = dataItems[0];
      } else if (!stillExists) {
        newAvailableSubjects.unshift({
          id: currentId,
          title: currentId,
        });
      }
    }

    this.availableSubjects.set(newAvailableSubjects);

    // Store content channels keyed by subject for per-subject filtering
    this.contentChannelsBySubject = {};
    Object.values(report.items).forEach((item) => {
      this.contentChannelsBySubject[String(item.subjectValue)] = item.contentChannels;
    });

    if (this.currentRow) {
      this.onSubjectClick(this.currentRow);
    }
  }

  onSubjectClick(row: JourneyRow): void {
    this.currentRow = row;
    const subjectId = String(row.subjectId);

    const subjectChannels = this.contentChannelsBySubject[subjectId] ?? [];
    const subjectChannelIds = subjectChannels.map((cc) => cc.id);

    // If the new date range returns data for this patient, update the dropdown options
    if (subjectChannels.length > 0 || this.availableContentChannels().length === 0) {
      this.suppressChannelFilterChange = true;
      this.availableContentChannels.set(
        subjectChannels.map((cc) => ({ id: cc.id, title: cc.label ?? cc.id }))
      );
      // Restore selection from persisted deselected channels; default to all selected on first open
      const deselected = this.payload.deselectedChannelFilter ?? [];
      const selectedFromPayload = subjectChannelIds.filter((id) => !deselected.includes(id));
      this.selectedContentChannels.set(
        selectedFromPayload.length > 0 ? selectedFromPayload : subjectChannelIds
      );
      setTimeout(() => {
        this.suppressChannelFilterChange = false;
      });
    }

    this.payload.numericMetadataFields.forEach((f, i) => {
      f.color = ChartCustomColorService.colorStringToRgbaColor(
        DEFAULT_PROJECT_COLOR_PALETTE[i % DEFAULT_PROJECT_COLOR_PALETTE.length]
      );
    });

    this.nmotWidget.set(null);
    this.timelineWidget.set(null);
    this.sharedReportResponse.set(null);
    this.pendingTimelineWidget = null;
    this.buildJourneyTimelineWidgets(subjectId, subjectChannelIds);
  }

  private buildJourneyTimelineWidgets(subjectId: string, subjectChannelIds: string[]): void {
    // Fresh clones start at the journey level, so any timeline drill state is dropped and the NMOT
    // chart must query the report itself again instead of reusing a drilled timeline response.
    this.nmotDrillDateFilterValue = null;
    this.timelinePreviousDateInterval.set(null);
    this.nmotPreloadedResponse.set(null);
    const currentNmotLegends = this.nmotWidget()?.payload.legends ?? {};
    const currentTimelineLegends = this.timelineWidget()?.payload.legends ?? {};
    const nmotWidget = JSON.parse(JSON.stringify(this.widget)) as Widget<WidgetChartOptions>;
    const tlWidget = JSON.parse(JSON.stringify(this.widget)) as Widget<WidgetChartOptions>;
    nmotWidget.id = `${nmotWidget.id}-nmot`;
    const nmotWidgetPayload = nmotWidget.payload as any;
    const timelineWidgetPayload = tlWidget.payload as any;

    const timelineFields = this.widget.payload.numericMetadataFields
      .filter((f) => f.showOnTimeline !== false)
      .map((f) => ({ ...f, scoring: 'LAST' }));

    nmotWidgetPayload.numericMetadataFields = timelineFields;

    nmotWidgetPayload.type = 'numericMetadata' as any;
    nmotWidgetPayload.graphType = this.payload.graphType ?? 'spline';
    nmotWidgetPayload.journeyTimelineRole = 'nmot';
    nmotWidgetPayload.isDrilledDown = true;
    nmotWidgetPayload.drilledDownLocation = subjectId;
    nmotWidgetPayload.subject = this.defaultField;
    nmotWidgetPayload.subjectValue = subjectId;
    nmotWidgetPayload.dateInterval = this.payload.dateInterval ?? 'month';
    nmotWidgetPayload.legends = { ...currentNmotLegends };
    nmotWidgetPayload.customFieldSelected = true;
    // Filter out comments for selected subjects only (this is needed because @onTimelineSubjectChange function)
    nmotWidgetPayload.comments = this.payload.comments?.filter(
      (c) => c.target.subjectId === subjectId
    );
    const selectedChannelIds = this.selectedContentChannels();
    const isAllChannelsSelected =
      subjectChannelIds.length === 0 ||
      subjectChannelIds.every((id) => selectedChannelIds.includes(id));
    const effectiveChannelFilter = isAllChannelsSelected ? [] : selectedChannelIds;
    const channelFilterValue =
      effectiveChannelFilter.length > 0 ? effectiveChannelFilter : undefined;

    nmotWidgetPayload.contentChannelFilter = channelFilterValue;

    timelineWidgetPayload.numericMetadataFields = timelineFields;
    timelineWidgetPayload.type = 'numericMetadata' as any;
    timelineWidgetPayload.graphType = 'timeline';
    timelineWidgetPayload.journeyTimelineRole = 'touchpoints';
    timelineWidgetPayload.dateInterval = this.payload.dateInterval ?? 'month';
    timelineWidgetPayload.isDrilledDown = true;
    timelineWidgetPayload.subject = this.defaultField;
    timelineWidgetPayload.subjectValue = subjectId;
    timelineWidgetPayload.legends = { ...currentTimelineLegends };
    timelineWidgetPayload.contentChannelParentMap = this.contentChannelParentMap;
    timelineWidgetPayload.contentChannelLabelMap = this.contentChannelLabelMap;
    timelineWidgetPayload.contentChannelFilter = channelFilterValue;

    this.nmotWidget.set(nmotWidget);
    this.timelineSubject.set(subjectId);

    if (this.sharedReportResponse()) {
      // Re-render (e.g. chart options change): reuse the existing response for both charts.
      this.timelineWidget.set(tlWidget);
    } else {
      // First load: defer the timeline widget until the NMOT report response arrives.
      // This avoids a duplicate API call: the NMOT chart makes the call, and the timeline
      // reuses its response via preloadedReportResponse.
      this.pendingTimelineWidget = tlWidget;
      this.timelineWidget.set(null);
    }
    this.widgetComponent.updateTemplateBooleans();
    this.widgetComponent.initWidgetActions();
    this.timelineLegendItems.set(
      timelineFields.map((field) => ({
        color: `rgba(${field.color.red}, ${field.color.green}, ${field.color.blue}, ${field.color.alpha})`,
        label: field.label,
      }))
    );
    requestAnimationFrame(() => this.updateCompactMode());
  }

  onNmotReportResponseReady(response: any): void {
    if (this.pendingTimelineWidget) {
      // First load: store the shared response and activate the timeline widget.
      // Subsequent NMOT re-renders (e.g. drill-down) must not update the shared response
      // because from then on the timeline owns the report call for the displayed drill level.
      this.sharedReportResponse.set(response);
      this.timelineWidget.set(this.pendingTimelineWidget);
      this.pendingTimelineWidget = null;
    }
  }

  onTimelineLoadingStarted(initialLoad: boolean): void {
    // A new timeline query invalidates the response the NMOT chart is allowed to reuse.
    this.latestTimelineResponse = null;
    this.loadingStarted.emit(initialLoad);
  }

  onTimelineReportResponseReady(response: any): void {
    this.latestTimelineResponse = response;
  }

  toggleSettingsPopup(): void {
    this.showSettingsPopup.update((v) => !v);
  }

  closeSettingsPopup(): void {
    // If showSettingsPopup is open and the calendar popup is open we don't close the settings popup yet
    const calendarPopup = document.querySelector('.kxi-calendar-component-wrapper');
    if (calendarPopup) {
      return;
    }
    if (this.showSettingsPopup()) {
      this.showSettingsPopup.set(false);
    }
  }

  closeTimelineView(): void {
    this.currentRow = null;
    this.nmotWidget.set(null);
    this.timelineSubject.set(null);
    this.widgetComponent.updateTemplateBooleans();
    this.widgetComponent.initWidgetActions();
    this.timelineWidget.set(null);
    this.pendingTimelineWidget = null;
    this.sharedReportResponse.set(null);
    this.nmotPreloadedResponse.set(null);
    this.latestTimelineResponse = null;
    this.selectedContentChannels.set([]);
    this.timelineLegendItems.set([]);
    this.timelinePreviousDateInterval.set(null);
    this.nmotDrillDateFilterValue = null;
    this.isCompact.set(false);
    this.showSettingsPopup.set(false);
  }

  async backToFullTimeline(): Promise<void> {
    await this.timelineChart()?.restoreClusteredView();
  }

  onChartLoadingFinished(initialLoad: boolean): void {
    if (initialLoad && !this.commentsSeeded()) {
      this.commentsSeeded.set(true);
      this.payload.comments?.forEach((comment) => this.onCommentAdded(comment));
    }
    this.loadingFinished.emit(initialLoad);
    this.syncTimelinePlotArea();
  }

  public override shouldRefreshAfterChartOptionsChange(_options: ChartDisplayOptions): boolean {
    // Journey chart options are presentation-only for cloned timeline/nmot widgets.
    // They never change the underlying report query/data, so we must not trigger a data refresh.
    return false;
  }

  public override handleChartOptionsChanging(): void {
    const row = this.currentRow;
    if (row) {
      const subjectId = String(row.subjectId);
      const subjectChannelIds = this.getSubjectChannelIds(subjectId);
      this.buildJourneyTimelineWidgets(subjectId, subjectChannelIds);
    }
    this.redraw();
  }

  public override redraw(): void {
    requestAnimationFrame(() => {
      this.nmotChart()?.redraw();
      this.timelineChart()?.redraw();
      this.syncTimelinePlotArea();
      setTimeout(() => this.updateCompactMode(), 300);
    });
  }

  /**
   * Mirrors the timeline drill level onto the NMOT chart: same date interval and bucket date filter,
   * so both charts render the buckets the backend returned for that level. The timeline's response
   * is reused, so no second report call is made for it.
   */
  onTimelineAxisZoomChanged(range: JourneyDrillRange | null): void {
    const nmotWidget = this.nmotWidget();
    if (nmotWidget) {
      const nmotPayload = nmotWidget.payload as any;
      delete nmotPayload.expandedJourneyRange;
      delete nmotPayload.expandedJourneyDisplayRange;
      delete nmotPayload.expandedJourneyDateInterval;
      this.removeNmotDrillDateFilter(nmotWidget);
      this.timelinePreviousDateInterval.set(null);
      nmotPayload.dateInterval = this.payload.dateInterval ?? 'month';
      // The timeline has just executed the report with the parameters the NMOT chart needs, so its
      // response is handed over instead of issuing an identical second call. Both inputs change in
      // the same tick, so the NMOT chart refreshes once.
      this.nmotPreloadedResponse.set(this.latestTimelineResponse);
      if (range) {
        nmotPayload.expandedJourneyRange = { min: range.dataMin, max: range.dataMax };
        nmotPayload.expandedJourneyDisplayRange = { min: range.min, max: range.max };
        nmotPayload.expandedJourneyDateInterval = range.dateInterval;
        nmotPayload.dateInterval = range.dateInterval;
        nmotPayload.filters.dates.and.push({
          analyticItem: 'createdAt',
          key: 'daterange',
          value: range.dateFilter,
        });
        this.nmotDrillDateFilterValue = range.dateFilter;
        this.timelinePreviousDateInterval.set(range.previousDateInterval);
      }
      this.nmotWidget.set({ ...nmotWidget });
    }
    this.nmotChart()?.setXAxisExtremes(range);
  }

  private removeNmotDrillDateFilter(nmotWidget: Widget<WidgetChartOptions>): void {
    if (!this.nmotDrillDateFilterValue) {
      return;
    }
    const dateFilters = nmotWidget.payload.filters.dates.and;
    const idx = dateFilters.findIndex((f) => f.value === this.nmotDrillDateFilterValue);
    if (idx !== -1) {
      dateFilters.splice(idx, 1);
    }
    this.nmotDrillDateFilterValue = null;
  }

  private resetPagination(): void {
    this.subjectAfterKey.set('');
    this.pageIndex.set(0);
    this.pageAfterKeys = [''];
  }

  /**
   * Keeps the touchpoint timeline's plot area on the NMOT chart's, so a timestamp lands on the same
   * pixel in both. Runs after every NMOT render because its margins also change without a reload,
   * e.g. on resize or when drilled scores widen the y-axis labels.
   */
  syncTimelinePlotArea(): void {
    if (this.plotAreaSyncTimer) {
      return;
    }
    this.plotAreaSyncTimer = setTimeout(() => {
      this.plotAreaSyncTimer = null;
      const nmotChart = this.nmotChart();
      const timelineChart = this.timelineChart();
      if (!nmotChart || !timelineChart) {
        return;
      }
      timelineChart.syncPlotAreaFrom(nmotChart);
    });
  }

  onSearchChanged(searchText: string): void {
    this.searchText.set(searchText.trim());
    this.resetPagination();
    this.refresh(false, false);
  }

  onCalendarValueChange(event: Event): void {
    const calendarData: Calendar_onValueChangeEvent = (event as CustomEvent).detail;

    const updateCalendar = (
      mode: 'relative' | 'fixed',
      first: Date,
      last: Date,
      optionId: string = null
    ) => {
      this.calendarMode.set(mode);
      this.calendarCheckedId.set(optionId);
      this.calendarData.update((d) => ({
        ...d,
        initialDateRange: { first, last },
      }));
    };

    if (calendarData.optionId) {
      // Relative date option selected (e.g. "All", "Last 7 Days", etc.)
      this.widget.payload.selectedRelativeRange = calendarData.optionId;
      delete this.widget.payload.customDateRange;

      updateCalendar(
        'relative',
        calendarData.dates?.first,
        calendarData.dates?.last,
        calendarData.optionId
      );
    } else if (calendarData.dates?.first && calendarData.dates?.last) {
      // Custom fixed date range selected
      this.widget.payload.selectedRelativeRange = 'custom';
      this.widget.payload.customDateRange = {
        startDate: calendarData.dates.first,
        endDate: calendarData.dates.last,
      };

      updateCalendar('fixed', calendarData.dates.first, calendarData.dates.last);
    }

    this.dashboardStore.markDirty();
    this.resetPagination();
    this.refresh(false, false);
  }

  onTimelineSubjectChange(subjectId: string): void {
    if (this.timelineSubject() === subjectId) {
      return;
    }

    const row = this.dataSource().find((r) => String(r.subjectId) === subjectId);
    if (row) {
      this.onSubjectClick(row);
    }
  }

  onContentChannelFilterChange(selected: DropdownObject[]): void {
    if (this.suppressChannelFilterChange) {
      return;
    }
    const selectedIds = (selected ?? []).map((s) => s.id);
    if (this.hasSameIds(selectedIds, this.selectedContentChannels())) {
      return;
    }

    this.selectedContentChannels.set(selectedIds);

    // Persist deselected channels to widget payload so settings survive across subjects and sessions
    const availableIds = this.availableContentChannels().map((c) => c.id);
    const newlyDeselected = availableIds.filter((id) => !selectedIds.includes(id));
    const existingDeselected = this.payload.deselectedChannelFilter ?? [];
    const updatedDeselected = [
      ...existingDeselected.filter((id) => !selectedIds.includes(id)),
      ...newlyDeselected.filter((id) => !existingDeselected.includes(id)),
    ];
    (this.widget.payload as any).deselectedChannelFilter = updatedDeselected;

    if (this.currentRow) {
      this.onSubjectClick(this.currentRow);
    }
  }

  onViewJourneyDetails(): void {
    const currentRow = this.dataSource().find(
      (r) => String(r.subjectId) === this.timelineSubject()
    );
    if (!currentRow) {
      return;
    }

    const selectedChannels = this.selectedContentChannels();
    let channelFilter;
    let totalTouchpoints: number;
    if (selectedChannels.length === 1) {
      channelFilter = {
        id: selectedChannels[0],
        label: this.contentChannelLabelMap[selectedChannels[0]] ?? selectedChannels[0],
      };
      totalTouchpoints = (currentRow[selectedChannels[0]] as number) ?? currentRow.totalTouchpoints;
    } else if (selectedChannels.length > 1) {
      totalTouchpoints = selectedChannels.reduce(
        (sum: number, id: string) => sum + ((currentRow[id] as number) ?? 0),
        0
      );
    } else {
      totalTouchpoints = currentRow.totalTouchpoints;
    }

    const subjectChannelIds = this.getSubjectChannelIds(String(currentRow.subjectId));
    const allowedChannelIds =
      selectedChannels.length > 0
        ? subjectChannelIds.filter((id) => selectedChannels.includes(id))
        : subjectChannelIds;
    this.journeyDetailsDialogOptions.set({
      subjectId: String(currentRow.subjectId),
      subjectField: this.defaultField,
      channelFilter,
      allowedChannelIds,
      totalTouchpoints,
      widgetDataSource: this.widgetDataSource,
      numericMetadataFields: this.payload.numericMetadataFields,
      dashboard: this.dashboard,
      project: this.project,
      baseQuery: this.query,
    });
    this.showJourneyDetailsDialog.set(true);
  }

  isAnalyticItemColumn(column: string): boolean {
    return this.analyticItemColumns.has(column);
  }

  getColumnHeader(column: string): string {
    return this.columns()[column]?.label ?? column;
  }

  getCellMenuData(row, column, extraData) {
    return {
      item: row?.[column],
      itemDisplayName: row?.[column],
      row,
      column,
      extraData,
    };
  }

  executeReportForCsv(_result: DownloadAsDialogResult) {
    return Promise.resolve(this.dataSource());
  }

  prepareDataForCsv(data) {
    return data;
  }

  getCommentsForPoint(point): Comment[] {
    return this.payload.comments?.filter((c) => c.target?.pointId === point?.id) ?? [];
  }

  onPageChanged(event: PageEvent): void {
    if (event.pageSize !== this.pageSize()) {
      this.pageSize.set(event.pageSize);
      this.resetPagination();
      this.refresh(false, false);
      return;
    }

    const newPageIndex = event.pageIndex;
    const targetAfterKey = this.pageAfterKeys[newPageIndex] ?? '';
    this.subjectAfterKey.set(targetAfterKey);
    this.pageIndex.set(newPageIndex);
    this.refresh(false, false);
  }

  openJourneyDetails(row: JourneyRow, channelId?: string): void {
    const channelFilter =
      channelId && this.columns()[channelId]
        ? { id: channelId, label: this.columns()[channelId].label }
        : undefined;

    const subjectChannelIds = this.getSubjectChannelIds(String(row.subjectId));
    this.journeyDetailsDialogOptions.set({
      subjectId: row.subjectId,
      subjectField: this.defaultField,
      channelFilter,
      allowedChannelIds: subjectChannelIds,
      totalTouchpoints: channelId ? ((row[channelId] as number) ?? 0) : row.totalTouchpoints,
      widgetDataSource: this.widgetDataSource,
      numericMetadataFields: this.payload.numericMetadataFields,
      dashboard: this.dashboard,
      project: this.project,
      baseQuery: this.filterService.getQueryForDocuments(this.widgetQueryOptions, false, true),
    });
    this.showJourneyDetailsDialog.set(true);
  }

  onJourneyDetailsDialogClosed(): void {
    this.showJourneyDetailsDialog.set(false);
  }

  onJourneyDocumentsChanged(): void {
    this.isDirty = true;
  }

  isClickableColumn(column: string): boolean {
    return (
      column === 'totalTouchpoints' ||
      (!this.isAnalyticItemColumn(column) && column !== 'subjectId')
    );
  }

  isCellClickable(row: JourneyRow, column: string): boolean {
    if (!this.isClickableColumn(column)) {
      return false;
    }
    const value = column === 'totalTouchpoints' ? row.totalTouchpoints : (row[column] as number);
    return value > 0;
  }

  private formatScoringLabel(scoring: string): string {
    switch (scoring) {
      case 'AVERAGE':
        return translate('widgets.journey.average');
      case 'LAST':
        return translate('widgets.journey.final-value');
      case 'DELTA':
        return translate('widgets.journey.delta');
      default:
        return scoring;
    }
  }

  private buildChildToParentMap(
    node: ContentChannel,
    parent?: ContentChannel,
    map: Record<string, string> = {}
  ): Record<string, string> {
    if (parent) {
      map[node.id] = parent.id;
    }
    for (const child of node.children ?? []) {
      this.buildChildToParentMap(child, node, map);
    }
    return map;
  }

  private clearJourneyData(closeTimelineView = true): void {
    this.noData = true;
    this.dataSource.set([]);
    this.availableSubjects.set([]);
    this.availableContentChannels.set([]);
    this.contentChannelsBySubject = {};

    if (closeTimelineView) {
      this.closeTimelineView();
    }
  }

  private getSubjectChannelIds(subjectId: string): string[] {
    return (this.contentChannelsBySubject[subjectId] ?? []).map((channel) => channel.id);
  }

  private getJourneyDrillIntervalLabelKey(dateInterval: string): string {
    switch (dateInterval) {
      case 'hour':
      case 'minute':
      case 'touchpoint':
        return 'time';
      default:
        return dateInterval;
    }
  }

  private buildIdToLabelMap(
    node: ContentChannel,
    map: Record<string, string> = {}
  ): Record<string, string> {
    map[node.id] = node.label;
    for (const child of node.children ?? []) {
      this.buildIdToLabelMap(child, map);
    }
    return map;
  }

  private setupResizeObserver(): void {
    let debounceTimer: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => this.updateCompactMode(), 300);
    });
    observer.observe(this.elementRef.nativeElement);
    this.destroyRef.onDestroy(() => {
      observer.disconnect();
      clearTimeout(debounceTimer);
      clearTimeout(this.plotAreaSyncTimer);
    });
    requestAnimationFrame(() => this.updateCompactMode());
  }

  private updateCompactMode(): void {
    if (!this.timelineSubject()) {
      this.isCompact.set(false);
      this.showSettingsPopup.set(false);
      return;
    }

    const hostElement = this.elementRef.nativeElement as HTMLElement;
    const timelineControls = hostElement.querySelector(
      '.widget-journey__timeline-controls'
    ) as HTMLElement | null;

    if (!timelineControls) {
      return;
    }

    const compact = timelineControls.getBoundingClientRect().width < 960;
    this.isCompact.set(compact);
    if (!compact) {
      this.showSettingsPopup.set(false);
    }
  }

  private hasSameIds(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    const rightSet = new Set(right);
    return left.every((id) => rightSet.has(id));
  }

  // As of 30.06.2026 we are not allowing comparisons nor widget groups to journey widgets

  canAddToComparison(): boolean {
    return false;
  }

  canApplyComparison(): boolean {
    return false;
  }

  canAddToWidgetGroup(): boolean {
    return false;
  }

  override get canAddComment(): boolean {
    return !!this.timelineSubject();
  }

  override actionsDisabled(): boolean {
    return this.widgetComponent.isExpansionPanelShowing;
  }

  async exportAsImage(isPpt?: boolean): Promise<Blob> {
    const containerEl = this.nmotChart()
      ? this.chartContainer.nativeElement
      : this.table.nativeElement;
    const currentScrollTop = containerEl.scrollTop;

    return toCanvas(containerEl, { backgroundColor: 'white' }).then((canvas) => {
      if (!isPpt) {
        canvas = this.createNewCanvasWithTitle(canvas);
      }

      const contentDataURL = canvas.toDataURL('image/svg');

      const body = {
        filename: this.defaultTitle,
        svg: canvas,
      };

      containerEl.scroll(0, currentScrollTop);

      return firstValueFrom(
        this.httpClient.post<Blob>(contentDataURL, body, {
          responseType: 'blob' as 'json',
        })
      );
    });
  }

  async exportAsPptx(pptx: PptxReport): Promise<void> {
    const blob = await this.exportAsImage(false);
    return this.exportImageToPptx(blob, pptx, this.widgetTitle);
  }

  onCommentAdded(comment: Comment) {
    this.chartCommentsService.addComment(
      this.nmotChart().elementContent.nativeElement,
      comment,
      this.widget.payload.commentsHidden,
      this.annotationClickHandlerFn(),
      this.annotationDragHandlerFn()
    );
  }

  onCommentEdited(comment: Comment): void {
    this.chartCommentsService.editComment(
      this.nmotChart().elementContent.nativeElement,
      comment,
      this.widget.payload.commentsHidden,
      this.annotationClickHandlerFn(),
      this.annotationDragHandlerFn()
    );
  }

  onCommentDeleted(comment: Comment): void {
    const isCommentVisible = this.chartCommentsService.removeComment(
      this.nmotChart().elementContent.nativeElement,
      comment
    );
    if (!isCommentVisible) {
      return;
    }
    const nextComment = this.widget.payload.comments.find(
      (c) => c.target?.pointId === comment.target?.pointId && c.id !== comment.id
    );
    if (nextComment) {
      this.chartCommentsService.addComment(
        this.nmotChart().elementContent.nativeElement,
        nextComment,
        this.widget.payload.commentsHidden,
        this.annotationClickHandlerFn(),
        this.annotationDragHandlerFn()
      );
    }
  }

  isCommentVisible(comment: Comment): boolean {
    return this.chartCommentsService.isCommentVisible(
      this.nmotChart().elementContent.nativeElement,
      comment
    );
  }

  getVisibleComments(): Comment[] {
    if (this.widget.payload.commentsHidden) {
      return [];
    }
    return this.chartCommentsService.getVisibleComments(
      this.nmotChart().elementContent.nativeElement,
      this.payload.comments
    );
  }

  annotationClickHandlerFn() {
    const self = this;
    return function () {
      const hcPoint = this.labels[0].points[0];
      const point = {
        point: { x: hcPoint.x, y: hcPoint.y, xAxis: 0, yAxis: 0 },
        pointId: hcPoint.id,
        series: hcPoint.series?.custom?.seriesId ?? hcPoint.series?.name,
      };
      self.pointComments = self.getCommentsForPoint(hcPoint);
      self.commentsHandlerService.showExpansionPanel({
        widgetId: self.widget.id,
        widgetContainer: self.widgetComponent.widgetContainer,
        panelData: point,
        panelType: 'COMMENTS',
      });
    };
  }

  override hideOrShowAllComments(): void {
    const nmotChartEl = this.nmotChart()?.elementContent?.nativeElement;
    if (!nmotChartEl) {
      return;
    }
    this.chartCommentsService.hideOrShowComments(nmotChartEl, this.widget.payload.commentsHidden);
  }

  itemToCommentPoint(item): CommentPoint {
    return {
      point: {
        x: item.x,
        y: item.y,
        xAxis: 0,
        yAxis: 0,
      },
      pointId: item.id,
      subjectId: this.timelineSubject(),
      series: item.series?.custom?.seriesId ?? item.series?.name ?? item.category,
    };
  }
}
